import { P } from 'components'
import CustomButton from 'components/Buttons/CustomButton'
import LoadingAnimation from 'components/LoadingAnimation'
import PMBInputNumber from 'components/PMBInputNumber'
import PMBSelect from 'components/PMBSelect'
import PMBTotalFooter from 'components/PMBTotalFooter'
import {
  evaluateRule,
  generateHumanReadableSummary,
  normalizeRule,
} from 'features/private-views/map-schema/schemaRuleUtils'
import { useFetchFindoraInventoryListQuery } from 'features/private-views/product-mapping-schema/productMappingSchemaListApiSlice'
import RuleGroupedRecipes from 'features/private-views/product-mapping-schema/RuleGroupedRecipes'
import { useLazyFetchWarehousesQuery } from 'features/private-views/transfer-inventory/transferInventoryApiSlice'
import { capitalizeEveryWord, formatNumber, parseNumber } from 'helpers'
import { useSnackbar } from 'notistack'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertCircle, AlertTriangle, ArrowLeft, Box, Minus, Plus, Zap } from 'react-feather'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Badge, Button, Card, CardBody, Container, Input, Modal, ModalBody, ModalHeader, Spinner } from 'reactstrap'
import { useAutoPopulateAll } from 'features/private-views/product-mapping-schema/hooks/useAutoPopulateAll'
import { parseFormattedNumber } from 'utility/Utils'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import { URL_PROCESS } from 'utils/pathUrl'
import { shakeInvalidFields } from 'utils/shakeInvalidFields'
import {
  useAddSchemaMutation,
  useFetchSchemaDetailQuery,
} from '../processApiSlice'

export const getInventoryWarehouseId = (inv) => {
  if (!inv) return null
  const id = inv.warehouseId ?? inv.customLabel?.warehouseId ?? null
  if (id != null && String(id).trim() !== '') return String(id).trim()
  const name = inv.warehouseName || inv.customLabel?.warehouseName || null
  if (name && String(name).trim() !== '' && String(name).trim() !== '-') {
    return `name:${String(name).trim().toLowerCase()}`
  }
  return null
}

export const checkMaterialsWarehouseValid = (
  materials = [],
  inventorySplitData = {},
) => {
  const activeInventories = (materials || []).flatMap((mat) => {
    const splits = inventorySplitData[mat.id] || []
    if (splits.length > 0) {
      return (mat.selectedInventories || []).filter((inv) => {
        const split = splits.find(
          (s) => String(s.inventoryId) === String(inv.id || inv.value),
        )
        return split ? Number(split.splitAmount) > 0 : true
      })
    }
    return mat.selectedInventories && mat.selectedInventories.length > 0
      ? mat.selectedInventories
      : mat.selectedInventory && parseNumber(mat.qty) > 0
        ? [mat.selectedInventory]
        : []
  })

  if (activeInventories.length === 0) return true

  // Collect all warehouse IDs using consistent getInventoryWarehouseId helper
  const wids = activeInventories
    .map(getInventoryWarehouseId)
    .filter((id) => id != null)

  if (wids.length <= 1) return true

  const firstWh = wids[0]
  return wids.every((wh) => wh === firstWh)
}
const ProcessPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const { sessionProcess, selectedSchema } = useSelector((s) => s.auth)
  const processDirection = location.state?.processDirection || 'forward'
  const isBackward = processDirection === 'backward'
  const [step, setStep] = useState(1)
  const [materials, setMaterials] = useState([])
  const [output, setOutput] = useState([])
  const [notes, setNotes] = useState('')
  const [warehouse, setWarehouse] = useState(null)
  const [listWarehouse, setListWarehouse] = useState(null)
  const [loading, setLoading] = useState(false)
  const [inventorySplitData, setInventorySplitData] = useState({})
  const [searchTerms, setSearchTerms] = useState({})
  const [isConfirmUnfilledOpen, setIsConfirmUnfilledOpen] = useState(false)
  const [showErrors, setShowErrors] = useState(false)

  const applyScannerSearchTerm = useCallback(
    (productName) => {
      const searchValue = String(productName || '').trim()
      if (!searchValue || !Array.isArray(materials) || materials.length === 0) {
        return
      }

      // Find which material this search term likely belongs to by checking names
      const normalizedSearch = searchValue.toLowerCase()
      const matchedMaterial = materials.find((m) =>
        String(m?.name || '')
          .toLowerCase()
          .includes(normalizedSearch),
      )

      // Fallback to the first material if no name match is found
      const targetMaterial = matchedMaterial || materials[0]
      if (!targetMaterial?.id) return

      setSearchTerms((prev) => ({
        ...prev,
        [targetMaterial.id]: searchValue,
      }))

      enqueueSnackbar(
        `🔍 Mencari "${searchValue}" untuk ${targetMaterial?.name || 'material'}...`,
        { variant: 'info', autoHideDuration: 2000 },
      )
    },
    [materials, enqueueSnackbar],
  )

  const [addSchema, response] = useAddSchemaMutation()

  const [getGudang, resWarehouse] = useLazyFetchWarehousesQuery()

  const { data, isLoading, status } = useFetchSchemaDetailQuery(
    {
      // itemId: selectedSchema?.itemId,
      id: selectedSchema?.id,
    },
    { skip: !selectedSchema },
  )

  const schemaRule = useMemo(
    () => normalizeRule(data?.rule || selectedSchema?.rule),
    [data?.rule, selectedSchema?.rule],
  )

  const itemIds = useMemo(() => {
    return materials?.map((m) => m.itemId) || []
  }, [materials])

  useEffect(() => {
    if (!data) return

    setMaterials(
      data.input?.map((item) => ({
        id: item.itemId,
        itemId: item.itemId,
        name: item.nameItem,
        nameItem: item.nameItem,
        required: item.reqAmount,
        requiredAfterCals: item.reqAmount,
        qty: item.reqAmount,
        unit: item.expectedUnit || item.unit,
        expectedUnit: item.expectedUnit || item.unit,
        selectedInventory: null,
        selectedInventories: [],
        ...item,
      })) || [],
    )

    setOutput(
      data.output?.map((item) => ({
        ...item,
        required: item?.amount,
      })) || [],
    )
  }, [data])

  const {
    data: dataFindoraInventoryList,
    isLoading: isLoadingFindoraInventoryList,
    refetch: refetchFindoraInventoryList,
  } = useFetchFindoraInventoryListQuery(
    {
      itemIds: itemIds,
    },
    {
      skip: itemIds.length === 0,
    },
  )

  const recipes = useMemo(() => {
    return (materials || []).map((m) => ({
      id: m.id || m.itemId,
      itemId: m.itemId || m.id,
      nameItem: m.name || m.nameItem,
      reqAmount: m.requiredAfterCals ?? m.required ?? 0,
      unit: m.expectedUnit ?? m.unit ?? 'Kg',
      expectedUnit: m.expectedUnit ?? m.unit ?? 'Kg',
    }))
  }, [materials])

  const [singleLoadingId, setSingleLoadingId] = useState(null)

  const handleApplyAutoPopulate = useCallback(
    (finalGroups) => {
      if (!finalGroups || finalGroups.length === 0) return

      setMaterials((prevMaterials) => {
        const nextMaterials = prevMaterials.map((mat) => {
          const matchingGroup = finalGroups.find(
            (g) => String(g.id || g.itemId) === String(mat.id || mat.itemId),
          )
          if (!matchingGroup) return mat

          const validEntries = (matchingGroup.entries || []).filter(
            (entry) => entry.inventoryId && Number(entry.amount) > 0,
          )

          if (validEntries.length === 0) {
            return {
              ...mat,
              selectedInventories: [],
              selectedInventory: null,
            }
          }

          const selectedInventories = validEntries.map((e) => {
            const invOpt = e.inventoryId
            return {
              ...invOpt,
              id: invOpt.id || invOpt.value,
              value: invOpt.value || invOpt.id,
            }
          })

          return {
            ...mat,
            selectedInventories,
            selectedInventory: selectedInventories[0] || null,
          }
        })

        setInventorySplitData((prevSplit) => {
          const updatedSplit = { ...prevSplit }

          finalGroups.forEach((matchingGroup) => {
            const matchedMat = prevMaterials.find(
              (m) =>
                String(m.id || m.itemId) ===
                  String(matchingGroup.id || matchingGroup.itemId) ||
                String(m.itemId || m.id) ===
                  String(matchingGroup.itemId || matchingGroup.id),
            )
            const matId = matchedMat
              ? matchedMat.id
              : matchingGroup.id || matchingGroup.itemId

            const validEntries = (matchingGroup.entries || []).filter(
              (entry) => entry.inventoryId && Number(entry.amount) > 0,
            )

            if (validEntries.length === 0) {
              delete updatedSplit[matId]
            } else {
              updatedSplit[matId] = validEntries.map((e) => ({
                inventoryId: e.inventoryId.id || e.inventoryId.value,
                splitAmount: Number(e.amount),
              }))
            }
          })

          if (!isBackward) {
            recalculateOutput(nextMaterials, updatedSplit)
          }

          return updatedSplit
        })

        return nextMaterials
      })

      refetchFindoraInventoryList?.()

      enqueueSnackbar('Auto Populate berhasil diterapkan.', {
        variant: 'success',
      })
    },
    [isBackward, refetchFindoraInventoryList, enqueueSnackbar],
  )

  const autoPopulateAll = useAutoPopulateAll({
    rule: schemaRule,
    quantity: 1,
    recipes,
    onApply: handleApplyAutoPopulate,
  })

  const handleAutoPopulateSingle = async (materialItem) => {
    try {
      setSingleLoadingId(materialItem.id)

      const currentItemId = Number(materialItem.itemId || materialItem.id)
      let reqAmount =
        materialItem.requiredAfterCals ?? materialItem.required ?? 0

      // Guard schema rule for single auto populate:
      if (schemaRule?.groups) {
        // If materialItem is in an XOR group:
        const xorGroup = schemaRule.groups.find(
          (g) => g.op === 'XOR' && g.itemIds.includes(currentItemId),
        )
        if (xorGroup) {
          const otherMemberFilled = materials.find((m) => {
            if (Number(m.itemId || m.id) === currentItemId) return false
            if (!xorGroup.itemIds.includes(Number(m.itemId || m.id))) return false
            const splits = inventorySplitData[m.id] || []
            if (splits.length > 0) {
              return splits.some((s) => Number(s.splitAmount) > 0)
            }
            return m.selectedInventories?.length > 0
          })
          if (otherMemberFilled) {
            enqueueSnackbar(
              `Bahan substitusi lain (${otherMemberFilled.name || otherMemberFilled.nameItem || 'lain'}) sudah dipilih untuk grup ini.`,
              { variant: 'warning' },
            )
            return
          }
        }

        // If materialItem is in an OR group:
        const orGroup = schemaRule.groups.find(
          (g) => g.op === 'OR' && g.itemIds.includes(currentItemId),
        )
        if (orGroup) {
          const groupMembers = materials.filter((m) =>
            orGroup.itemIds.includes(Number(m.itemId || m.id)),
          )
          const groupNeed = Math.max(
            ...groupMembers.map((m) =>
              Number(m.requiredAfterCals ?? m.required ?? 0),
            ),
          )
          const otherAllocated = groupMembers
            .filter((m) => Number(m.itemId || m.id) !== currentItemId)
            .reduce((sum, m) => {
              const splits = inventorySplitData[m.id] || []
              const total = splits.reduce(
                (s, sp) => s + Number(sp.splitAmount || 0),
                0,
              )
              return sum + total
            }, 0)

          const remainingGroupNeed = Math.max(0, groupNeed - otherAllocated)
          if (remainingGroupNeed <= 0) {
            enqueueSnackbar(
              'Kebutuhan untuk grup campuran ini sudah terpenuhi oleh bahan lain.',
              { variant: 'info' },
            )
            return
          }
          reqAmount = Math.min(Number(reqAmount), remainingGroupNeed)
        }
      }

      const otherMaterials = materials.filter((m) => m.id !== materialItem.id)
      const otherInventories = otherMaterials.flatMap(
        (m) => m.selectedInventories || [],
      )
      const usedInventoryIds = [
        ...otherInventories.map((inv) => inv.id || inv.value),
        ...otherMaterials.flatMap((m) =>
          (inventorySplitData[m.id] || []).map((s) => s.inventoryId),
        ),
      ].filter(Boolean)

      const otherWithWh = otherInventories.find(
        (inv) => getInventoryWarehouseId(inv) != null,
      )
      const activeWarehouse = otherWithWh
        ? {
            id:
              otherWithWh.warehouseId ??
              otherWithWh.customLabel?.warehouseId ??
              null,
            name:
              otherWithWh.warehouseName ||
              otherWithWh.customLabel?.warehouseName ||
              null,
          }
        : null

      const targetRecipe = {
        id: materialItem.id || materialItem.itemId,
        itemId: materialItem.itemId || materialItem.id,
        nameItem: materialItem.name || materialItem.nameItem,
        reqAmount,
        unit: materialItem.expectedUnit ?? materialItem.unit ?? 'Kg',
        expectedUnit:
          materialItem.expectedUnit ?? materialItem.unit ?? 'Kg',
      }

      await autoPopulateAll.runForRecipe(targetRecipe, {
        activeWarehouse,
        usedInventoryIds,
      })
    } catch (error) {
      console.error('Single auto populate error:', error)
      enqueueSnackbar(
        error?.message || error || 'Gagal melakukan Auto Populate material.',
        { variant: 'error' },
      )
    } finally {
      setSingleLoadingId(null)
    }
  }

  const recalculateOutput = (currentMaterials, currentSplitData) => {
    let minRatio = null

    if (schemaRule?.groups?.length) {
      const groupedItemIds = new Set()

      schemaRule.groups.forEach((group) => {
        const groupItems = currentMaterials.filter((m) =>
          group.itemIds.includes(Number(m.id)),
        )
        groupItems.forEach((m) => groupedItemIds.add(Number(m.id)))

        if (group.op === 'OR') {
          const totalQty = groupItems.reduce((sum, m) => {
            const splits = currentSplitData[m.id] || []
            return (
              sum +
              splits.reduce(
                (sSum, split) => sSum + (Number(split.splitAmount) || 0),
                0,
              )
            )
          }, 0)
          const groupRequired = Math.max(
            ...groupItems.map((m) => parseNumber(m.required || 0)),
          )
          if (totalQty > 0 && groupRequired > 0) {
            const ratio = totalQty / groupRequired
            if (minRatio === null || ratio < minRatio) {
              minRatio = ratio
            }
          }
        } else {
          groupItems.forEach((m) => {
            const required = parseNumber(m?.required ?? 0)
            if (required > 0) {
              const splitData = currentSplitData[m.id]
              if (splitData && splitData.length > 0) {
                const totalQty = splitData.reduce(
                  (sum, split) => sum + (Number(split.splitAmount) || 0),
                  0,
                )
                if (totalQty > 0) {
                  const ratio = totalQty / required
                  if (minRatio === null || ratio < minRatio) {
                    minRatio = ratio
                  }
                }
              }
            }
          })
        }
      })

      currentMaterials
        .filter((m) => !groupedItemIds.has(Number(m.id)))
        .forEach((m) => {
          const required = parseNumber(m?.required ?? 0)
          if (required > 0) {
            const splitData = currentSplitData[m.id]
            if (splitData && splitData.length > 0) {
              const totalQty = splitData.reduce(
                (sum, split) => sum + (Number(split.splitAmount) || 0),
                0,
              )
              if (totalQty > 0) {
                const ratio = totalQty / required
                if (minRatio === null || ratio < minRatio) {
                  minRatio = ratio
                }
              }
            }
          }
        })
    } else {
      currentMaterials.forEach((m) => {
        const required = parseNumber(m?.required ?? 0)
        if (required > 0) {
          const splitData = currentSplitData[m.id]
          if (splitData && splitData.length > 0) {
            const totalQty = splitData.reduce(
              (sum, split) => sum + (Number(split.splitAmount) || 0),
              0,
            )

            if (totalQty > 0) {
              const ratio = totalQty / required
              if (minRatio === null || ratio < minRatio) {
                minRatio = ratio
              }
            }
          }
        }
      })
    }

    if (minRatio !== null) {
      setOutput((prevOutput) =>
        prevOutput?.map((out) => ({
          ...out,
          amount: out?.required ? out.required * minRatio : out?.amount,
        })),
      )
    }
  }

  const recalculateInput = (currentOutput) => {
    let minRatio = null

    currentOutput.forEach((out) => {
      const required = parseNumber(out?.required ?? 0)
      const current = parseNumber(out?.amount ?? 0)
      if (required > 0 && current > 0) {
        const ratio = current / required
        if (minRatio === null || ratio < minRatio) {
          minRatio = ratio
        }
      }
    })

    if (minRatio !== null) {
      setMaterials((prevMaterials) =>
        prevMaterials?.map((m) => ({
          ...m,
          requiredAfterCals: m?.required ? m.required * minRatio : m?.requiredAfterCals,
          qty: m?.required ? m.required * minRatio : m?.qty,
        }))
      )
    }
  }

  const handleSelectInventory = (itemId, selectedInventory) => {
    const material = materials?.find((m) => m.id === itemId)
    const requiredAmount = parseNumber(material?.requiredAfterCals ?? material?.required ?? 0)

    const newMaterials = materials?.map((m) =>
      m.id === itemId
        ? {
          ...m,
          selectedInventory: selectedInventory,
          selectedInventories: selectedInventory ? [selectedInventory] : [],
          qty: selectedInventory ? Math.min(requiredAmount, selectedInventory.amount) : m.qty,
        }
        : m,
    )
    setMaterials(newMaterials)

    if (selectedInventory) {
      setInventorySplitData((prev) => {
        const updatedSplitData = {
          ...prev,
          [itemId]: [
            {
              inventoryId: selectedInventory.id,
              splitAmount: Math.min(requiredAmount, selectedInventory.amount),
            },
          ],
        }
        if (!isBackward) recalculateOutput(newMaterials, updatedSplitData)
        return updatedSplitData
      })
    } else {
      setInventorySplitData((prev) => {
        const updated = { ...prev }
        delete updated[itemId]
        if (!isBackward) recalculateOutput(newMaterials, updated)
        return updated
      })
    }
  }

  const handleSelectInventories = (
    itemId,
    selectedInventories,
    dynamicRequiredAmount,
  ) => {
    const material = materials?.find((m) => m.id === itemId)
    const requiredAmount =
      dynamicRequiredAmount != null
        ? parseNumber(dynamicRequiredAmount)
        : parseNumber(material?.requiredAfterCals ?? material?.required ?? 0)

    const newMaterials = materials?.map((m) =>
      m.id === itemId
        ? {
          ...m,
          selectedInventories: selectedInventories || [],
          selectedInventory: selectedInventories?.[0] || null,
        }
        : m,
    )
    setMaterials(newMaterials)

    if (selectedInventories && selectedInventories.length > 0) {
      setInventorySplitData((prev) => {
        let remainingRequired = requiredAmount
        const newSplitData = selectedInventories.map((inv) => {
          const existing = prev[itemId]?.find((i) => i.inventoryId === inv.id)
          
          let amountToUse = 0
          if (existing?.splitAmount != null) {
            amountToUse = parseNumber(existing.splitAmount)
          } else {
            amountToUse = Math.min(Math.max(0, remainingRequired), inv.amount)
          }
          
          remainingRequired -= amountToUse
          
          return {
            inventoryId: inv.id,
            splitAmount: amountToUse,
          }
        })
        const updatedSplitData = {
          ...prev,
          [itemId]: newSplitData,
        }
        if (!isBackward) recalculateOutput(newMaterials, updatedSplitData)
        return updatedSplitData
      })
    } else {
      setInventorySplitData((prev) => {
        const updated = { ...prev }
        delete updated[itemId]
        if (!isBackward) recalculateOutput(newMaterials, updated)
        return updated
      })
    }
  }

  const handleQtyChange = (id, value) => {
    const newMaterials = materials?.map((m) => (m.id === id ? { ...m, qty: value } : m))
    setMaterials(newMaterials)
    if (!isBackward) recalculateOutput(newMaterials, inventorySplitData)
  }

  const handleInventoryAmountChange = (itemId, inventoryId, value) => {
    const numericValue = parseFormattedNumber(value)

    setInventorySplitData((prev) => {
      const updatedSplitData = {
        ...prev,
        [itemId]: prev[itemId]?.map((item) =>
          item.inventoryId === inventoryId
            ? { ...item, splitAmount: numericValue }
            : item,
        ),
      }
      if (!isBackward) recalculateOutput(materials, updatedSplitData)
      return updatedSplitData
    })
  }

  const handleOutputChange = (newValue) => {
    setOutput(newValue)
  }

  const unfilledMaterials = useMemo(() => {
    return (
      materials?.filter((m) => {
        const splits = inventorySplitData[m.id]
        if (splits && splits.length > 0) {
          return !splits.some(
            (s) => s.inventoryId && parseNumber(s.splitAmount) > 0,
          )
        }
        return !(
          m?.selectedInventories?.length > 0 ||
          (m?.selectedInventory && parseNumber(m.qty) > 0)
        )
      }) || []
    )
  }, [materials, inventorySplitData])

  const suppliedItemIds = useMemo(() => {
    return (materials || [])
      .filter((m) => {
        const splits = inventorySplitData[m.id]
        if (splits && splits.length > 0) {
          return splits.some(
            (s) => s.inventoryId && parseNumber(s.splitAmount) > 0,
          )
        }
        return (
          m?.selectedInventories?.length > 0 ||
          (m.selectedInventory &&
            (m.selectedInventory.id || m.selectedInventory.value) &&
            parseNumber(m.qty) > 0)
        )
      })
      .map((m) => Number(m.id))
  }, [materials, inventorySplitData])

  const ruleVerdict = useMemo(() => {
    return schemaRule ? evaluateRule(schemaRule, suppliedItemIds) : null
  }, [schemaRule, suppliedItemIds])

  const isWarehouseValid = useMemo(() => {
    return checkMaterialsWarehouseValid(materials, inventorySplitData)
  }, [materials, inventorySplitData])

  const isMaterialValid = isWarehouseValid

  const recipesForRules = useMemo(() => {
    return (materials || []).map((m) => {
      const splits = inventorySplitData[m.id] || []
      const allocated =
        splits.length > 0
          ? splits.reduce(
              (sum, s) => sum + parseNumber(s.splitAmount || 0),
              0,
            )
          : m.selectedInventory
            ? parseNumber(m.qty || 0)
            : 0
      const entries =
        splits.length > 0
          ? splits.map((s) => ({
              inventoryId: s.inventoryId,
              amount: parseNumber(s.splitAmount || 0),
            }))
          : m.selectedInventory
            ? [
                {
                  inventoryId: m.selectedInventory.id,
                  amount: parseNumber(m.qty || 0),
                },
              ]
            : []
      return {
        ...m,
        id: m.id,
        nameItem: m.nameItem || m.name || '-',
        label: m.name || m.nameItem || '-',
        linkFoto: m.linkFoto || m.image,
        image: m.linkFoto || m.image,
        reqAmount: parseNumber(m.requiredAfterCals ?? m.required ?? 0),
        unit: m.expectedUnit || m.unit || 'Kg',
        allocatedAmount: allocated,
        entries,
      }
    })
  }, [materials, inventorySplitData])

  const handleConfirmOutput = () => {
    setStep(2)
  }

  const handleConfirmMaterial = () => {
    if (!isMaterialValid) {
      enqueueSnackbar('Semua material harus berasal dari gudang yang sama.', {
        variant: 'error',
        preventDuplicate: true,
      })
      return
    }

    if (schemaRule) {
      if (!ruleVerdict?.ok) {
        setShowErrors(true)
        setTimeout(shakeInvalidFields, 50)
        enqueueSnackbar(
          ruleVerdict?.message || 'Pilih inventori untuk minimal satu material',
          { variant: 'error', preventDuplicate: true },
        )
        return
      }
      setStep(2)
      return
    }

    if (suppliedItemIds.length === 0) {
      setShowErrors(true)
      setTimeout(shakeInvalidFields, 50)
      enqueueSnackbar('Pilih inventori untuk minimal satu material', {
        variant: 'error',
        preventDuplicate: true,
      })
      return
    }

    if (unfilledMaterials.length > 0) {
      setIsConfirmUnfilledOpen(true)
      return
    }
    setStep(2)
  }

  const handleProcess = () => {
    if (!isMaterialValid) {
      enqueueSnackbar('Semua material harus berasal dari gudang yang sama.', {
        variant: 'error',
        preventDuplicate: true,
      })
      return
    }

    if (schemaRule && !ruleVerdict?.ok) {
      setShowErrors(true)
      setTimeout(shakeInvalidFields, 50)
      enqueueSnackbar(
        ruleVerdict?.message || 'Pilih inventori untuk minimal satu material',
        { variant: 'error', preventDuplicate: true },
      )
      return
    }

    if (isBackward) {
      if (suppliedItemIds.length === 0) {
        setShowErrors(true)
        setTimeout(shakeInvalidFields, 50)
        enqueueSnackbar('Pilih inventori untuk minimal satu material', {
          variant: 'error',
          preventDuplicate: true,
        })
        return
      }

      if (!schemaRule && unfilledMaterials.length > 0) {
        setIsConfirmUnfilledOpen(true)
        return
      }
    }
    executeProcess()
  }

  const executeProcess = async () => {
    try {
      const res = await addSchema({
        itemId: selectedSchema?.itemId,
        output: output?.map((e) => {
          return {
            itemId: e?.itemId,
            amount: parseNumber(e?.amount),
          }
        }),
        id: selectedSchema?.id,
        input: materials
          ?.filter(
            (e) =>
              inventorySplitData[e.id]?.length > 0 ||
              e?.selectedInventories?.length > 0 ||
              e?.selectedInventory,
          )
          ?.flatMap((e) => {
            // Support multi-select: use split data if available
            const splitData = inventorySplitData[e.id]
            if (splitData && splitData.length > 0) {
              return splitData
                .filter((split) => Number(split.splitAmount) > 0)
                .map((split) => ({
                  inventoryId: split.inventoryId,
                  amount: parseNumber(split.splitAmount),
                }))
            }
            // Fallback to single selection
            return {
              inventoryId:
                e?.selectedInventory?.id || e?.selectedInventory?.value,
              amount: parseNumber(e?.qty),
            }
          })
          .filter((item) => item.inventoryId && Number(item.amount) > 0),
        note: notes,
      })
      if (res?.error) {
        throw res
      }
      enqueueSnackbar(`${selectedSchema?.label} berhasil.`, {
        variant: 'success',
      })
      navigate(URL_PROCESS)
    } catch (error) {
      enqueueSnackbar(error?.message ?? error?.error?.data?.message, {
        variant: 'error',
      })
    }
  }

  const inventoryById = useMemo(() => {
    if (!dataFindoraInventoryList || !Array.isArray(dataFindoraInventoryList)) {
      return {}
    }

    const grouped = {}

    dataFindoraInventoryList.forEach((inventory) => {
      const itemId = inventory.itemId || inventory.item?.id
      if (!grouped[itemId]) {
        grouped[itemId] = []
      }

      const itemName = inventory.item?.nameItem || inventory.nameItem || '-'
      const ownerCode = inventory.ownerCode || '-'
      const lotCode = inventory.lotCode || '-'
      const amount = inventory.amount || 0
      const unit = inventory.unit || '-'
      const warehouseName = inventory.warehouseName || '-'
      const groupLabel = inventory.groupLabel || '-'

      const label = `${itemName} | Kode Inventori: ${ownerCode} | Lot ID: ${lotCode} | ${newFormatThousandSeparator(amount || 0)} ${unit} | Gudang: ${warehouseName} | Grup: ${groupLabel}`

      grouped[itemId].push({
        value: inventory.id,
        label: label,
        image: inventory.item?.linkFoto || inventory.linkFoto,
        customLabel: {
          itemName,
          ownerCode,
          lotCode,
          amount,
          unit,
          warehouseName,
          groupLabel,
        },
        ...inventory,
      })
    })

    return grouped
  }, [dataFindoraInventoryList])

  const getInventoryOptionsForRecipe = (item) => {
    const itemId = item?.itemId
    if (!itemId) return []
    const options = inventoryById[itemId] || []
    if (!item?.unit) return options
    const itemUnit = String(item?.expectedUnit ?? item?.unit)
      .toLowerCase()
      .trim()

    const filteredOptions = options.filter((opt) => {
      const optUnit = (opt && (opt.unit || opt.customLabel?.unit)) || ''
      return String(optUnit).toLowerCase().trim() === itemUnit
    })

    // Jika hasil filter berdasarkan strict unit kosong (contohnya karena perbedaan nama unit misal kg vs gram),
    // kita kembalikan raw options (semua inventori dari itemId tersebut) agar dropdown tidak hilang
    return filteredOptions.length > 0 ? filteredOptions : options
  }

  const formatOptionLabel = (option) => {
    if (!option.customLabel) return option.label

    const {
      itemName,
      ownerCode,
      lotCode,
      amount,
      unit,
      warehouseName,
      groupLabel,
    } = option.customLabel

    return (
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
        <div
          style={{ width: '100%', wordWrap: 'break-word' }}
          className="mb-2 mb-md-0"
        >
          <div>{itemName}</div>
          <div
            className="text-muted"
            style={{ fontSize: '0.85em', marginTop: '2px' }}
          >
            <span>Kode Inventori: {ownerCode ?? '-'}</span>
            <span className="ms-2 me-2">•</span>
            <span>Lot ID: {lotCode ?? '-'}</span>
          </div>
        </div>
        <div
          className="text-muted d-flex flex-wrap align-items-center justify-content-start justify-content-md-end"
          style={{ width: '100%', gap: '0.5rem' }}
        >
          <span
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {newFormatThousandSeparator(amount || 0)}{' '}
            {capitalizeEveryWord(unit)}
          </span>
          <span className="d-none d-md-inline">|</span>
          <span style={{ wordWrap: 'break-word' }}>{warehouseName}</span>
        </div>
      </div>
    )
  }

  // useEffect(() => {
  //   let isMounted = true
  //   setLoading(true)

  //   const fetchData = async () => {
  //     try {
  //       let resGudangAsal = await getGudang({ isForFilter: 1 })
  //       if (
  //         isMounted &&
  //         resGudangAsal?.data?.totalItems > resGudangAsal?.data?.items?.length
  //       ) {
  //         const updatedResGudangAsal = await getGudang({
  //           isForFilter: 1,
  //           size: resGudangAsal?.data?.totalItems,
  //         })
  //         if (updatedResGudangAsal?.data) {
  //           resGudangAsal = updatedResGudangAsal
  //         }
  //       }
  //       if (isMounted) {
  //         setListWarehouse(resGudangAsal?.data?.newData)
  //       }
  //     } catch (error) {
  //       //
  //     } finally {
  //       setLoading(false)
  //     }
  //   }

  //   fetchData()

  //   return () => {
  //     isMounted = false
  //   }
  // }, [])\

  useEffect(() => {
    const onScannerProductSearch = (event) => {
      applyScannerSearchTerm(event?.detail?.productName)
    }

    document.addEventListener('scanner:product-search', onScannerProductSearch)
    return () => {
      document.removeEventListener(
        'scanner:product-search',
        onScannerProductSearch,
      )
    }
  }, [applyScannerSearchTerm])

  return (
    <>
      <div className="pb-5 mb-5 px-3 w-100 position-relative">
        <Container className="custom-container pb-5 mb-5">
          <div className="d-flex align-items-center gap-2 mb-4">
            <Button
              color="link"
              className="p-0 text-dark"
              onClick={() => {
                setShowErrors(false)
                if (step === 2) {
                  setStep(1)
                } else {
                  navigate(-1)
                }
              }}
              data-testid="process-back-button"
            >
              <ArrowLeft size={18} />
            </Button>
            <h5 className="fw-bold mb-0">{selectedSchema?.label}</h5>
            <Badge
              color={isBackward ? 'warning' : 'primary'}
              className="ms-2"
              style={{ fontSize: '0.7rem' }}
            >
              {isBackward ? 'Kebelakang' : 'Kedepan'}
            </Badge>
          </div>

          {isLoading || status === 'pending' ? (
            <LoadingAnimation />
          ) : (
            <>
              {/* ===== OUTPUT SECTION ===== */}
              {step === (isBackward ? 1 : 2) && (
                <>
                  <p className="text-muted mb-3">
                    Masukkan jumlah output dan catatan proses
                  </p>

                  {output?.map((e, i) => {
                    return (
                      <Card
                        key={`${e?.itemId}-${i}`}
                        className="shadow-sm border-0 mb-3"
                        data-testid={`process-form-output-${e?.itemId}`}
                      >
                        <CardBody>
                          <div className="mb-2 d-flex gap-2 flex-column">
                            <div>
                              <h6 className="fw-bold mb-0">{e?.nameItem}</h6>
                              <Badge
                                size="sm"
                                disabled
                                className="badge-blues mt-1"
                              >
                                Default: {e?.required} {e?.expectedUnit ?? e?.unit}
                              </Badge>
                            </div>
                            <div className="d-flex align-items-center gap-2">
                              <Button
                                color="light"
                                onClick={() => {
                                  const parsedOutput = parseNumber(e?.amount)

                                  const newValue = Math.max(0, parsedOutput - 1)

                                  const newOutput = [...output]
                                  newOutput[i] = {
                                    ...newOutput[i],
                                    amount: newValue,
                                  }
                                  setOutput(newOutput)
                                  if (isBackward) recalculateInput(newOutput)
                                }}
                              >
                                <Minus size={16} />
                              </Button>

                              <PMBInputNumber
                                onFocus={(e) => e.target.select()}
                                name={`output-amount-${i}`}
                                placeholder="Jumlah"
                                prefix={capitalizeEveryWord(e?.unit)}
                                isFirstPrefix={false}
                                value={newFormatThousandSeparator(
                                  e?.amount || 0,
                                )}
                                onChange={(event) => {
                                  const numericValue = parseFormattedNumber(
                                    event.target.value,
                                  )
                                  const newOutput = [...output]
                                  newOutput[i] = {
                                    ...newOutput[i],
                                    amount: numericValue,
                                  }
                                  setOutput(newOutput)
                                  if (isBackward) recalculateInput(newOutput)
                                }}
                              />

                              <Button
                                color="light"
                                onClick={() => {
                                  const parsedOutput = parseNumber(e?.amount)

                                  const newValue = parsedOutput + 1

                                  const newOutput = [...output]
                                  newOutput[i] = {
                                    ...newOutput[i],
                                    amount: newValue,
                                  }
                                  setOutput(newOutput)
                                  if (isBackward) recalculateInput(newOutput)
                                }}
                              >
                                <Plus size={16} />
                              </Button>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    )
                  })}

                  {/* <Card className="shadow-sm border-0 mb-3">
                    <CardBody>
                      <h6 className="fw-semibold">Gudang (Opsional)</h6>
                      <PMBSelect
                        value={warehouse}
                        onChange={(e) => {
                          setWarehouse(e)
                        }}
                        options={
                          listWarehouse?.filter(
                            (wh) =>
                              Number(wh?.value) !== Number(warehouse?.value),
                          ) ?? []
                        }
                        placeholder="Pilih gudang..."
                        isSearchable={true}
                        filterOption={(option, inputValue) => {
                          if (!inputValue) return true
                          const label = option?.data?.label || ''
                          return label
                            .toLowerCase()
                            .includes(inputValue.toLowerCase())
                        }}
                        noOptionsMessage={() => 'Tidak ada gudang yang sesuai'}
                      />
                    </CardBody>
                  </Card> */}
                </>
              )}

              {/* ===== INPUT/MATERIAL SECTION ===== */}
              {step === (isBackward ? 2 : 1) && (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <div>
                      <h6 className="fw-semibold mb-1">Sumber Material</h6>
                      <p className="text-muted mb-0 small">
                        Pilih inventori untuk setiap material, pastikan setiap
                        material berasal dari gudang yang sama.
                      </p>
                    </div>

                    {/* Auto populate is backward-only */}
                    {isBackward && (
                      <Button
                        color="primary"
                        outline
                        size="sm"
                        className="d-inline-flex align-items-center gap-1 text-nowrap"
                        style={{ borderRadius: '8px' }}
                        onClick={autoPopulateAll.run}
                        disabled={autoPopulateAll.isProcessing}
                        title="Isi semua material otomatis sesuai aturan skema"
                        data-testid="process-auto-populate-all-button"
                      >
                        {autoPopulateAll.isProcessing ? (
                          <Spinner size="sm" />
                        ) : (
                          <Zap size={14} />
                        )}
                        Auto Populate Semua
                      </Button>
                    )}
                  </div>

                  <RuleGroupedRecipes
                    rule={schemaRule}
                    recipes={recipesForRules}
                    suppliedItemIds={suppliedItemIds}
                    showErrors={showErrors}
                    quantity={1}
                    renderRecipe={(item, idx, overrides) => {
                      const isInvalid = overrides?.isInvalid
                      return (
                        <Card
                          key={`${item.id}-${idx}`}
                          className={`shadow-sm border mb-0 ${isInvalid ? 'border-danger' : ''}`}
                          style={{
                            borderRadius: '10px',
                            borderColor: isInvalid ? '#ef4444' : '#e5e7eb',
                            backgroundColor: '#ffffff',
                          }}
                          data-testid={`process-form-material-${item.id}`}
                          data-invalid={isInvalid}
                        >
                          <CardBody className="p-3">
                            {/* Header material */}
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <div>
                                <h6 className="fw-semibold mb-1">
                                  {item.name || item.nameItem}
                                </h6>
                                <Badge
                                  size="sm"
                                  disabled
                                  className="badge-blues mt-1"
                                >
                                  Diperlukan:{' '}
                                  {newFormatThousandSeparator(
                                    item?.requiredAmount ??
                                      item?.requiredAfterCals ??
                                      item?.required ??
                                      0,
                                  )}{' '}
                                  {item?.expectedUnit ?? item?.unit}
                                </Badge>
                              </div>

                              {isBackward && (
                                <Button
                                  color="primary"
                                  outline
                                  size="sm"
                                  className="d-inline-flex align-items-center gap-1 text-nowrap"
                                  style={{ borderRadius: '6px', fontSize: '0.75rem' }}
                                  onClick={() => handleAutoPopulateSingle(item)}
                                  disabled={
                                    singleLoadingId === item.id ||
                                    autoPopulateAll.isProcessing
                                  }
                                  data-testid={`process-material-auto-populate-${item.id}`}
                                >
                                  {singleLoadingId === item.id ? (
                                    <Spinner size="sm" style={{ width: 12, height: 12 }} />
                                  ) : (
                                    <Zap size={12} />
                                  )}
                                  Auto Populate
                                </Button>
                              )}
                            </div>

                            {/* Multi-select dropdown */}
                            <PMBSelect
                              key={`multi-select-${item.id}`}
                              inputValue={searchTerms[item.id] || ''}
                              onInputChange={(val, { action }) => {
                                if (action === 'input-change') {
                                  setSearchTerms((prev) => ({
                                    ...prev,
                                    [item.id]: val,
                                  }))
                                }
                              }}
                              value={item.selectedInventories || []}
                              options={getInventoryOptionsForRecipe(item)}
                              onChange={(selectedInventories) =>
                                handleSelectInventories(
                                  item.id,
                                  selectedInventories,
                                  item.requiredAmount,
                                )
                              }
                              getOptionLabel={(e) => e.label}
                              formatOptionLabel={formatOptionLabel}
                              placeholder="Pilih inventori"
                              isLoading={isLoadingFindoraInventoryList}
                              isMulti
                              menuPosition="fixed"
                              menuPlacement="bottom"
                              styles={{
                                control: (provided) => ({
                                  ...provided,
                                  borderColor: isInvalid
                                    ? '#ef4444'
                                    : '#d1d5db',
                                  boxShadow: 'none',
                                  '&:hover': {
                                    borderColor: isInvalid
                                      ? '#ef4444'
                                      : '#d1d5db',
                                  },
                                  borderRadius: '8px',
                                  padding: '2px',
                                  minWidth: '100%',
                                  maxWidth: '100%',
                                }),
                                valueContainer: (base) => ({
                                  ...base,
                                  maxHeight: 120,
                                  overflowY: 'auto',
                                }),
                                multiValue: (provided) => ({
                                  ...provided,
                                  backgroundColor: '#e0f2ff',
                                  borderRadius: '12px',
                                  padding: '2px 6px',
                                }),
                                multiValueLabel: (provided) => ({
                                  ...provided,
                                  color: '#0369a1',
                                }),
                                multiValueRemove: (provided) => ({
                                  ...provided,
                                  color: '#0369a1',
                                  '&:hover': {
                                    backgroundColor: '#b3e5fc',
                                    color: '#005f8e',
                                  },
                                }),
                                dropdownIndicator: (provided) => ({
                                  ...provided,
                                  color: '#6b7280',
                                }),
                                clearIndicator: (provided) => ({
                                  ...provided,
                                  color: '#6b7280',
                                }),
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999,
                                }),
                                option: (provided) => ({
                                  ...provided,
                                  padding: '8px 12px',
                                }),
                              }}
                            />

                            {/* Show selected inventories with individual amount inputs */}
                            {item?.selectedInventories?.length > 0 && (
                              <div className="mt-3">
                                <h6 className="fw-semibold mb-2">
                                  {item?.selectedInventories?.length} Inventori
                                  yang dipilih
                                </h6>
                                <div className="d-flex flex-column gap-2">
                                  {item.selectedInventories.map(
                                    (inv, invIdx) => {
                                      const splitData = inventorySplitData[
                                        item.id
                                      ]?.find((s) => s.inventoryId === inv.id)
                                      return (
                                        <div
                                          key={`${inv.id}-${invIdx}`}
                                          className="d-flex align-items-center justify-content-between p-2 border rounded gap-3"
                                          style={{
                                            backgroundColor: '#f9fafb',
                                          }}
                                        >
                                          <div className="d-flex align-items-start gap-2 flex-grow-1">
                                            {inv.linkFoto && (
                                              <img
                                                src={inv.linkFoto}
                                                alt={inv.nameItem}
                                                style={{
                                                  width: 40,
                                                  height: 40,
                                                  borderRadius: 8,
                                                  objectFit: 'cover',
                                                }}
                                              />
                                            )}
                                            <div className="flex-grow-1">
                                              <div className="fw-semibold">
                                                {inv.nameItem}
                                              </div>
                                              <div className="text-muted small">
                                                Kode Inventori: {inv.ownerCode}{' '}
                                                • Lot ID: {inv.lotCode || '-'}
                                              </div>
                                              <div className="text-muted small">
                                                Stok Tersedia:{' '}
                                                {newFormatThousandSeparator(
                                                  inv.amount || 0,
                                                )}{' '}
                                                {capitalizeEveryWord(inv.unit)}
                                              </div>
                                              <div className="text-muted small">
                                                Gudang:{' '}
                                                {inv.warehouseName || '-'}
                                              </div>
                                            </div>
                                          </div>

                                          <div style={{ width: 120 }}>
                                            <PMBInputNumber
                                              onFocus={(e) => e.target.select()}
                                              name={`amount-${inv.id}`}
                                              placeholder="Jumlah"
                                              prefix={capitalizeEveryWord(
                                                inv.unit,
                                              )}
                                              isFirstPrefix={false}
                                              maxValue={inv.amount}
                                              value={newFormatThousandSeparator(
                                                splitData?.splitAmount ??
                                                  inv.amount,
                                              )}
                                              onChange={(e) =>
                                                handleInventoryAmountChange(
                                                  item.id,
                                                  inv.id,
                                                  e.target.value,
                                                )
                                              }
                                            />
                                          </div>
                                        </div>
                                      )
                                    },
                                  )}
                                </div>
                              </div>
                            )}
                          </CardBody>
                        </Card>
                      )
                    }}
                  />
                </>
              )}

              {/* ===== NOTES + SUMMARY (ALWAYS STEP 2) ===== */}
              {step === 2 && (
                <>
                  <Card className="shadow-sm border-0 mb-3">
                    <CardBody>
                      <h6 className="fw-semibold">Catatan Proses (Opsional)</h6>
                      <Input
                        type="textarea"
                        rows="3"
                        placeholder="Tambahkan catatan atau observasi..."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        data-testid="process-form-notes"
                      />
                    </CardBody>
                  </Card>

                  {/* SUMMARY SECTION */}
                  <div className="mb-3">
                    <h6 className="fw-semibold mb-3">Ringkasan Proses</h6>

                    <Card className="border-0 shadow-sm mb-3">
                      <CardBody>
                        {/* Schema Name */}
                        <div className="mb-3">
                          <small className="text-muted d-block mb-1">
                            Nama Proses
                          </small>
                          <p className="fw-semibold mb-0">
                            {selectedSchema?.label || '-'}
                          </p>
                        </div>

                        {schemaRule && (
                          <div className="mb-3">
                            <small className="text-muted d-block mb-1">
                              Aturan Skema
                            </small>
                            <span className="badge bg-light text-dark border">
                              {
                                generateHumanReadableSummary(
                                  schemaRule,
                                  (id) =>
                                    materials.find(
                                      (m) => Number(m.id) === Number(id),
                                    )?.nameItem ||
                                    materials.find(
                                      (m) => Number(m.id) === Number(id),
                                    )?.name ||
                                    id,
                                ).formula
                              }
                            </span>
                          </div>
                        )}

                        {/* Output Summary */}
                        <div className="mb-3">
                          <small className="text-muted d-block mb-2">
                            Output yang Dihasilkan:
                          </small>
                          {output?.map((item, idx) => (
                            <div
                              key={idx}
                              className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom"
                            >
                              <div>
                                <p className="mb-0 fw-semibold">
                                  {item?.nameItem}
                                </p>
                                <small className="text-muted">
                                  Default: {formatNumber(item?.required)}{' '}
                                  {capitalizeEveryWord(item?.unit)}
                                </small>
                              </div>
                              <div className="text-end">
                                <p className="mb-0 fw-bold text-primary">
                                  {newFormatThousandSeparator(
                                    item?.amount || 0,
                                  )}{' '}
                                  {capitalizeEveryWord(item?.unit)}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Material Summary */}
                        <div className="mb-3">
                          <small className="text-muted d-block mb-2">
                            Material yang Digunakan:
                          </small>
                          {materials
                            ?.filter((item) => {
                              const splits = inventorySplitData[item.id] || []
                              if (splits.length > 0) {
                                return splits.some(
                                  (s) => Number(s.splitAmount) > 0,
                                )
                              }
                              return (
                                item?.selectedInventories?.length > 0 ||
                                (item?.selectedInventory &&
                                  Number(item?.qty) > 0)
                              )
                            })
                            ?.map((item) => (
                              <div
                                key={item.id}
                                className="mb-3 pb-3 border-bottom"
                              >
                                <p className="fw-semibold mb-2">{item.name}</p>

                                {item.selectedInventories
                                  ?.filter((inv) => {
                                    const splitData = inventorySplitData[
                                      item.id
                                    ]?.find((s) => s.inventoryId === inv.id)
                                    return (
                                      splitData == null ||
                                      Number(splitData.splitAmount) > 0
                                    )
                                  })
                                  ?.map((inv, idx) => {
                                  const splitData = inventorySplitData[
                                    item.id
                                  ]?.find((s) => s.inventoryId === inv.id)
                                  const qty = parseNumber(
                                    splitData?.splitAmount ?? 0,
                                  )
                                  return (
                                    <div
                                      key={`${inv.id}-${idx}`}
                                      className="mb-2 ms-3"
                                    >
                                      <small className="text-muted d-block">
                                        {inv.nameItem} - Kode Inventori:{' '}
                                        {inv.ownerCode} - Lot ID:{' '}
                                        {inv.lotCode || '-'}
                                      </small>
                                      <div className="d-flex flex-wrap gap-2 align-items-center mt-1">
                                        <span className="badge bg-light text-dark border">
                                          {newFormatThousandSeparator(qty || 0)}{' '}
                                          {capitalizeEveryWord(item?.unit)}
                                        </span>
                                        <span className="badge bg-light text-dark border">
                                          Gudang: {inv.warehouseName || '-'}
                                        </span>
                                      </div>
                                    </div>
                                  )
                                })}

                                <div className="mt-2">
                                  {(() => {
                                    const totalQty = (
                                      inventorySplitData[item.id] || []
                                    ).reduce(
                                      (sum, split) =>
                                        sum +
                                        parseNumber(split.splitAmount ?? 0),
                                      0,
                                    )
                                    return (
                                      <P
                                        bold
                                        size="medium"
                                        className="text-primary mt-2"
                                      >
                                        Total:{' '}
                                        {newFormatThousandSeparator(
                                          totalQty || 0,
                                        )}{' '}
                                        {capitalizeEveryWord(item?.unit)}
                                      </P>
                                    )
                                  })()}
                                </div>
                              </div>
                            ))}
                        </div>

                        {/* Notes Summary */}
                        {notes && (
                          <div>
                            <small className="text-muted d-block mb-1">
                              Catatan
                            </small>
                            <p className="mb-0 text-muted">{notes}</p>
                          </div>
                        )}
                      </CardBody>
                    </Card>
                  </div>
                </>
              )}
            </>
          )}
        </Container>
      </div>
      <PMBTotalFooter
        buttonOnly
        body={
          step === 1 ? (
            isBackward ? (
              <CustomButton
                block
                color="primary"
                onClick={handleConfirmOutput}
                testId="process-confirm-button"
              >
                Konfirmasi Output
              </CustomButton>
            ) : (
              <CustomButton
                block
                color="primary"
                disabled={isLoading || status === 'pending'}
                onClick={handleConfirmMaterial}
                testId="process-confirm-button"
              >
                {schemaRule
                  ? `Konfirmasi Material (${suppliedItemIds.length} Terpilih)`
                  : `Konfirmasi Material (${
                      materials?.filter(
                        (e) => e?.selectedInventories?.length > 0,
                      )?.length || 0
                    }/${selectedSchema?.numInput || materials?.length || 0})`}
              </CustomButton>
            )
          ) : (
            <CustomButton
              block
              color="primary"
              onClick={handleProcess}
              disabled={response.isLoading || loading}
              testId="process-submit-button"
            >
              <Box size={16} className="me-1" />
              Proses
            </CustomButton>
          )
        }
      />

      {/* CONFIRMATION MODAL FOR UNFILLED MATERIALS */}
      <Modal
        isOpen={isConfirmUnfilledOpen}
        toggle={() => setIsConfirmUnfilledOpen(false)}
        centered
        autoFocus={false}
        style={{ maxWidth: '480px' }}
        className="modal-dialog-centered"
      >
        <ModalHeader
          toggle={() => setIsConfirmUnfilledOpen(false)}
          className="border-bottom-0 pb-1 pt-3 px-3 px-md-4"
        >
          <div className="d-flex align-items-center gap-2">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
              style={{
                width: '38px',
                height: '38px',
                backgroundColor: '#fff7ed',
                border: '1px solid #ffedd5',
              }}
            >
              <AlertTriangle size={20} color="#ea580c" />
            </div>
            <div>
              <h5 className="modal-title fw-bold text-dark mb-0" style={{ fontSize: '1rem' }}>
                Material Belum Lengkap
              </h5>
              <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                Ada {unfilledMaterials.length} material yang belum dipilih inventorinya.
              </span>
            </div>
          </div>
        </ModalHeader>

        <ModalBody className="px-3 px-md-4 py-2">
          <div
            className="p-3 rounded-3 mb-3 d-flex align-items-start gap-2"
            style={{
              backgroundColor: '#fffbeb',
              border: '1px solid #fef3c7',
            }}
          >
            <AlertCircle size={16} color="#d97706" className="mt-1 flex-shrink-0" />
            <div style={{ fontSize: '0.82rem', color: '#92400e', lineHeight: '1.4' }}>
              Material berikut tidak diisi dan tidak akan digunakan dalam proses:
            </div>
          </div>

          <div className="d-flex flex-column gap-2 mb-3" style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {unfilledMaterials.map((item, idx) => (
              <div
                key={item.id || idx}
                className="p-2 rounded-2 border d-flex justify-content-between align-items-center"
                style={{ backgroundColor: '#f8fafc', fontSize: '0.82rem' }}
              >
                <div className="d-flex align-items-center gap-2">
                  <div className="fw-semibold text-dark">
                    {item.name || item.nameItem || '-'}
                  </div>
                </div>
                <div className="text-muted small">
                  Diperlukan: {newFormatThousandSeparator(item.requiredAfterCals ?? item.required ?? 0, 2)} {item.unit || '-'}
                </div>
              </div>
            ))}
          </div>

          <div className="text-dark small" style={{ fontSize: '0.82rem' }}>
            Apakah Anda yakin ingin tetap melanjutkan?
          </div>
        </ModalBody>

        <div className="modal-footer border-top p-3 gap-2">
          <Button
            color="secondary"
            outline
            className="px-3 border-0 text-muted"
            onClick={() => setIsConfirmUnfilledOpen(false)}
            style={{ borderRadius: '8px', fontSize: '0.85rem' }}
          >
            Periksa Kembali
          </Button>
          <Button
            color="primary"
            onClick={() => {
              setIsConfirmUnfilledOpen(false)
              if (step === 1 && !isBackward) {
                setStep(2)
              } else {
                executeProcess()
              }
            }}
            className="px-3 fw-bold"
            style={{ borderRadius: '8px', fontSize: '0.85rem' }}
          >
            Tetap Lanjutkan
          </Button>
        </div>
      </Modal>

      {/* AUTO POPULATE SPLIT CONFIRMATION MODAL */}
      {autoPopulateAll.confirmModal}
    </>
  )
}

export default ProcessPage
